import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { RequestDetailUiT } from "../domain/ui/requestDetail.model";
import type {
  UpdateVacationRequestResultT,
  UpdateVacationRequestsDataT,
} from "../domain/types/updateVacationRequests.types";
import type {
  VacationRequestDetailEntityT,
  VacationRequestEntityT,
} from "../domain/entities/vacationRequest.entity";
import type { VacationRequestsRepositoryT } from "../domain/ports/vacationRequests.repository";

import { getRequestDetail } from "../application/useCases/getRequestDetail";

import { updateVacationRequest } from "../application/useCases/updateVacationRequest";
import { applyVacationRequests } from "../application/useCases/applyVacationRequests";

import {
  getProcessedBy,
  getProcessedByLabel,
  mapAppliedRequests,
  mapOutUpdatedRequests,
  sortAuditHistoryAsc,
} from "./helpers";

const toDetailUiModel = (
  detail: VacationRequestDetailEntityT,
): RequestDetailUiT => {
  const auditHistory = sortAuditHistoryAsc(detail.audit?.history);

  return {
    ...detail,
    processedBy: getProcessedBy(detail),
    processedByLabel: getProcessedByLabel(detail.status),
    status: detail.status,
    collaborator: {
      ...detail.collaborator,
      schedule: detail.collaborator?.schedule ?? null,
    },
    audit: detail.audit ? { history: auditHistory } : null,
  };
};

type CreateUseRequestDetailParamsT = {
  vacationRequestsRepository: VacationRequestsRepositoryT;
};

type UseRequestDetailParamsT = {
  requestId: string;
  colaboraInstanceId: string;
};

export const createUseRequestDetail = ({
  vacationRequestsRepository,
}: CreateUseRequestDetailParamsT) => {
  return function useRequestDetail({
    requestId,
    colaboraInstanceId,
  }: UseRequestDetailParamsT) {
    const queryClient = useQueryClient();

    const query = useQuery({
      queryKey: ["vacations/requests/detail", { requestId }],
      queryFn: () =>
        getRequestDetail({
          vacationRequestsRepository,
          requestId,
          colaboraInstanceId,
        }),
      select: toDetailUiModel,
      enabled: !!requestId && !!colaboraInstanceId,
    });

    // La key real de la lista incluye los filtros (fechas/periodo) que el detalle no conoce; se usa
    // setQueriesData por prefijo para actualizar todas las variantes cacheadas (con la key exacta
    // incompleta el match fallaba y el cache nunca se actualizaba).
    const onSuccessUpdateList = ({
      succeeded,
    }: UpdateVacationRequestResultT) => {
      if (succeeded.length === 0) return;
      queryClient.setQueriesData<VacationRequestEntityT[]>(
        { queryKey: ["vacations/requests"] },
        (oldData) => {
          if (!oldData) return oldData;

          return mapOutUpdatedRequests(oldData, succeeded);
        },
      );
    };

    const approveRequest = useMutation({
      mutationFn: ({
        requestsToUpdate,
      }: {
        requestsToUpdate: UpdateVacationRequestsDataT[];
      }) =>
        updateVacationRequest({
          vacationRequestsRepository,
          data: requestsToUpdate,
          colaboraInstanceId,
        }),
      onSuccess: onSuccessUpdateList,
    });

    const rejectRequest = useMutation({
      mutationFn: ({
        requestsToUpdate,
      }: {
        requestsToUpdate: UpdateVacationRequestsDataT[];
      }) =>
        updateVacationRequest({
          vacationRequestsRepository,
          data: requestsToUpdate,
          colaboraInstanceId,
        }),
      onSuccess: onSuccessUpdateList,
    });

    // Aplicar pasa por POST /v1/incidence-requests (envia al sistema base), igual que en la tabla.
    const applyRequest = useMutation({
      mutationFn: ({ requestIds }: { requestIds: string[] }) =>
        applyVacationRequests({
          vacationRequestsRepository,
          requestIds,
        }),
      onSuccess: (data) => {
        const appliedIds = (data?.results ?? [])
          .filter((result) => result.isSuccess)
          .map((result) => result.requestId);

        if (appliedIds.length === 0) return;

        // Se actualiza el cache de la lista (todas sus variantes de filtros) en lugar de refetchear.
        queryClient.setQueriesData<VacationRequestEntityT[]>(
          { queryKey: ["vacations/requests"] },
          (oldData) => {
            if (!oldData) return oldData;

            return mapAppliedRequests(oldData, appliedIds);
          },
        );
        // El detalle solo se invalida (la respuesta del apply no trae processedBy/audit actualizados);
        // al estar cerrado no dispara red, se refresca si se vuelve a abrir.
        queryClient.invalidateQueries({
          queryKey: ["vacations/requests/detail", { requestId }],
        });
      },
    });

    return {
      ...query,
      approveRequest,
      rejectRequest,
      applyRequest,
    };
  };
};
